import practiceAreasData from '../../../../../shared/legal-taxonomy/practice_areas.json';
import phasesData from '../../../../../shared/legal-taxonomy/matter_phases.json';
import documentTypesData from '../../../../../shared/legal-taxonomy/document_types.json';
import mappingsData from '../../../../../shared/legal-taxonomy/practice_area_document_types.json';
import aliasesData from '../../../../../shared/legal-taxonomy/document_type_aliases.json';

export interface PracticeAreaTaxonomyEntry {
  key: string;
  label: string;
  aliases: string[];
}

export interface MatterPhaseTaxonomyEntry {
  key: string;
  label: string;
  cross_cutting: boolean;
}

export interface LegalDocumentTypeTaxonomyEntry {
  key: string;
  label: string;
  phase_key: string;
  universal: boolean;
  section_map_key: string;
  playbook_event_key: string;
  aliases: string[];
  classification_examples: string[];
}

export interface LegalDocumentTaxonomy {
  taxonomy_version: string;
  practice_areas: PracticeAreaTaxonomyEntry[];
  phases: MatterPhaseTaxonomyEntry[];
  document_types: LegalDocumentTypeTaxonomyEntry[];
  practice_area_document_types: Record<string, string[]>;
  aliases: Record<string, string>;
}

function normalizeLookup(input: string | null | undefined): string {
  return String(input || '')
    .trim()
    .toLowerCase()
    .replace(/[_/]+/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function normalizeKey(input: string | null | undefined): string {
  return normalizeLookup(input).replace(/\s+/g, '_');
}

export function loadLegalDocumentTaxonomy(): LegalDocumentTaxonomy {
  const versions = new Set([
    practiceAreasData.taxonomy_version,
    phasesData.taxonomy_version,
    documentTypesData.taxonomy_version,
    mappingsData.taxonomy_version,
    aliasesData.taxonomy_version,
  ]);

  if (versions.size !== 1) {
    throw new Error(`Taxonomy version mismatch: ${Array.from(versions).join(', ')}`);
  }

  return {
    taxonomy_version: practiceAreasData.taxonomy_version,
    practice_areas: practiceAreasData.practice_areas,
    phases: phasesData.phases,
    document_types: documentTypesData.document_types,
    practice_area_document_types: mappingsData.practice_area_document_types,
    aliases: aliasesData.aliases,
  };
}

export function normalizePracticeAreaKey(input: string | null | undefined): string | null {
  const taxonomy = loadLegalDocumentTaxonomy();
  const normalized = normalizeLookup(input);
  if (!normalized) return null;

  for (const practiceArea of taxonomy.practice_areas) {
    if (normalizeLookup(practiceArea.key) === normalized || normalizeLookup(practiceArea.label) === normalized) {
      return practiceArea.key;
    }
    if ((practiceArea.aliases || []).some((alias) => normalizeLookup(alias) === normalized)) {
      return practiceArea.key;
    }
  }

  return null;
}

export function resolveDocumentTypeKey(input: string | null | undefined): string | null {
  const taxonomy = loadLegalDocumentTaxonomy();
  const normalized = normalizeLookup(input);
  const normalizedKey = normalizeKey(input);
  if (!normalized) return null;

  const aliasTarget = taxonomy.aliases[normalized] || taxonomy.aliases[normalizedKey];
  if (aliasTarget) return aliasTarget;

  for (const documentType of taxonomy.document_types) {
    if (documentType.key === normalizedKey || normalizeLookup(documentType.label) === normalized) {
      return documentType.key;
    }
    if ((documentType.aliases || []).some((alias) => normalizeLookup(alias) === normalized || normalizeKey(alias) === normalizedKey)) {
      return documentType.key;
    }
  }

  return null;
}

export function listVisibleDocumentTypesForPracticeAreas(
  practiceAreaInputs: Array<string | null | undefined>,
): LegalDocumentTypeTaxonomyEntry[] {
  const taxonomy = loadLegalDocumentTaxonomy();
  const keys = new Set<string>();

  for (const documentType of taxonomy.document_types) {
    if (documentType.universal) keys.add(documentType.key);
  }

  for (const input of practiceAreaInputs) {
    const practiceAreaKey = normalizePracticeAreaKey(input);
    if (!practiceAreaKey) continue;
    for (const documentTypeKey of taxonomy.practice_area_document_types[practiceAreaKey] || []) {
      keys.add(documentTypeKey);
    }
  }

  return taxonomy.document_types
    .filter((documentType) => keys.has(documentType.key))
    .sort((left, right) => left.label.localeCompare(right.label));
}

export function getMatterPhaseLabel(phaseKey: string | null | undefined): string {
  const taxonomy = loadLegalDocumentTaxonomy();
  return taxonomy.phases.find((phase) => phase.key === phaseKey)?.label || String(phaseKey || '');
}

export function listMatterPhaseOptions(): Array<{ value: string; label: string; crossCutting: boolean }> {
  return loadLegalDocumentTaxonomy().phases.map((phase) => ({
    value: phase.key,
    label: phase.label,
    crossCutting: phase.cross_cutting,
  }));
}

export function listDocumentTypeOptionsForPracticeAreas(
  practiceAreaInputs: Array<string | null | undefined>,
): Array<{
  value: string;
  label: string;
  phaseKey: string;
  phaseLabel: string;
  universal: boolean;
}> {
  return listVisibleDocumentTypesForPracticeAreas(practiceAreaInputs).map((documentType) => ({
    value: documentType.key,
    label: documentType.label,
    phaseKey: documentType.phase_key,
    phaseLabel: getMatterPhaseLabel(documentType.phase_key),
    universal: documentType.universal,
  }));
}

export function listAllDocumentTypeOptions(): Array<{
  value: string;
  label: string;
  phaseKey: string;
  phaseLabel: string;
  playbookEventKey: string;
  universal: boolean;
}> {
  return loadLegalDocumentTaxonomy().document_types
    .map((documentType) => ({
      value: documentType.key,
      label: documentType.label,
      phaseKey: documentType.phase_key,
      phaseLabel: getMatterPhaseLabel(documentType.phase_key),
      playbookEventKey: documentType.playbook_event_key,
      universal: documentType.universal,
    }))
    .sort((left, right) => left.label.localeCompare(right.label));
}
